import { days } from 'src/app/components/lib/day-of-week-picker/button';

import { DiscountFirstTimeCustomer } from '../../models';

import type { DiscountPlatformTypeRestriction, PaymentRestrictions } from '../types';
import type { ActiveDaysMap } from 'src/app/components/lib/day-of-week-picker';

export enum DetailsStepActions {
  INITIALIZE = 'initialize',
  RESET = 'reset',
  SET_ACTIVE_DAYS_OF_THE_WEEK = 'set-active-days-of-the-week',
  SET_AVAILABLE_LOC_IDS = 'set-available-loc-ids',
  SET_AVAILABLE_ONLINE = 'set-available-online',
  SET_CUSTOMER_TYPES = 'set-customer-types',
  SET_DISCOUNT_GROUPS = 'set-discount-groups',
  SET_END_DATE = 'set-end-date',
  SET_END_TIME = 'set-end-time',
  SET_FIRST_TIME_CUSTOMER_OPTION = 'set-first-time-customer-option',
  SET_LIMIT_PER_CUSTOMER = 'set-limit-per-customer',
  SET_LIMIT_PER_CUSTOMER_QUANTITY = 'set-limit-per-customer-quantity',
  SET_ORDER_SOURCE_IDS = 'set-order-source-ids',
  SET_PAYMENT_RESTRICTION = 'set-payment-restriction',
  SET_REDEMPTION_LIMIT = 'set-redemption-limit',
  SET_REQUIRE_MANAGER_APPROVAL = 'set-require-manager-approval',
  SET_RESTRICT_BY_DAY_OF_WEEK = 'set-restrict-by-day-of-week',
  SET_SEGMENTS = 'set-segments',
  SET_SPECIFY_END_DATE = 'set-specify-end-date',
  SET_START_DATE = 'set-start-date',
  SET_START_TIME = 'set-start-time',
  VISITED = 'visited',
}

type InitializeAction = {
  payload: DetailsStepState;
  type: DetailsStepActions.INITIALIZE;
};

type ResetAction = {
  type: DetailsStepActions.RESET;
};

type SetActiveDaysOfTheWeekAction = {
  payload: ActiveDaysMap;
  type: DetailsStepActions.SET_ACTIVE_DAYS_OF_THE_WEEK;
};

type SetAvailableLocIdsAction = {
  payload: number[];
  type: DetailsStepActions.SET_AVAILABLE_LOC_IDS;
};

type SetAvailableOnlineAction = {
  payload: boolean;
  type: DetailsStepActions.SET_AVAILABLE_ONLINE;
};

type SetOrderSourceIdsAction = {
  payload: DiscountPlatformTypeRestriction[];
  type: DetailsStepActions.SET_ORDER_SOURCE_IDS;
};

type SetCustomerTypesAction = {
  payload: number[];
  type: DetailsStepActions.SET_CUSTOMER_TYPES;
};

type SetDiscountGroupsAction = {
  payload: number[];
  type: DetailsStepActions.SET_DISCOUNT_GROUPS;
};

type SetEndDateAction = {
  payload: Date;
  type: DetailsStepActions.SET_END_DATE;
};

type SetEndTimeAction = {
  payload: string;
  type: DetailsStepActions.SET_END_TIME;
};

type SetFirstTimeCustomerOptionAction = {
  payload: DiscountFirstTimeCustomer;
  type: DetailsStepActions.SET_FIRST_TIME_CUSTOMER_OPTION;
};

type SetLimitPerCustomerAction = {
  payload: boolean;
  type: DetailsStepActions.SET_LIMIT_PER_CUSTOMER;
};

type SetLimitPerCustomerQuantityAction = {
  payload: number | '';
  type: DetailsStepActions.SET_LIMIT_PER_CUSTOMER_QUANTITY;
};

type SetPaymentRestrictionAction = {
  payload: { enabled: boolean; restriction: keyof PaymentRestrictions };
  type: DetailsStepActions.SET_PAYMENT_RESTRICTION;
};

type SetRedemptionLimitAction = {
  payload: number | '';
  type: DetailsStepActions.SET_REDEMPTION_LIMIT;
};

type SetRequireManagerApprovalAction = {
  payload: boolean;
  type: DetailsStepActions.SET_REQUIRE_MANAGER_APPROVAL;
};

type SetRestrictByDayOfWeekAction = {
  payload: boolean;
  type: DetailsStepActions.SET_RESTRICT_BY_DAY_OF_WEEK;
};

type SetSegmentsAction = {
  payload: number[];
  type: DetailsStepActions.SET_SEGMENTS;
};

type SetSpecifyEndDateAction = {
  payload: boolean;
  type: DetailsStepActions.SET_SPECIFY_END_DATE;
};

type SetStartDateAction = {
  payload: Date;
  type: DetailsStepActions.SET_START_DATE;
};

type SetStartTimeAction = {
  payload: string;
  type: DetailsStepActions.SET_START_TIME;
};

type SetVisitedAction = {
  type: DetailsStepActions.VISITED;
};

export type DetailsStepAction =
  | InitializeAction
  | ResetAction
  | SetActiveDaysOfTheWeekAction
  | SetAvailableLocIdsAction
  | SetAvailableOnlineAction
  | SetCustomerTypesAction
  | SetDiscountGroupsAction
  | SetEndDateAction
  | SetEndTimeAction
  | SetFirstTimeCustomerOptionAction
  | SetLimitPerCustomerAction
  | SetLimitPerCustomerQuantityAction
  | SetOrderSourceIdsAction
  | SetPaymentRestrictionAction
  | SetRedemptionLimitAction
  | SetRequireManagerApprovalAction
  | SetRestrictByDayOfWeekAction
  | SetSegmentsAction
  | SetSpecifyEndDateAction
  | SetStartDateAction
  | SetStartTimeAction
  | SetVisitedAction;

export type DetailsStepState = {
  activeDaysOfTheWeek: ActiveDaysMap;
  availableLocIds: number[];
  availableOnline: boolean;
  customerTypeIds: number[];
  discountGroupIds: number[];
  endDate: Date | null;
  endTime: string;
  firstTimeCustomerOption: DiscountFirstTimeCustomer;
  limitPerCustomer: boolean;
  limitPerCustomerQuantity: number | '';
  paymentRestrictions: PaymentRestrictions;
  platformTypeRestrictions: DiscountPlatformTypeRestriction[];
  redemptionLimit: number | '';
  requireManagerApproval: boolean;
  restrictByDayOfWeek: boolean;
  segmentIds: number[];
  specifyEndDate: boolean;
  startDate: Date | null;
  startTime: string;
  visited: boolean;
};

/**
 * The type for Object.fromEntries is as follows:
 *   fromEntries<T = any>(entries: Iterable<readonly [PropertyKey, T]>): { [k: string]: T; };
 * Because k is of type string above and does not allow a type argument, we have to cast the type below.
 * A better type for fromEntries would be:
 *   fromEntries<T = any, U extends string>(entries: Iterable<readonly [PropertyKey, T]>): { [k: U]: T; };
 * This casting is safe because our argument to fromEntries (days) is the source of truth for our ActiveDaysMap type
 */
export const allDaysInactive = Object.fromEntries(days.map((day) => [day, false])) as ActiveDaysMap;

// Default start date should be the current date at midnight (to match the current functionality)
const defaultStartDate = new Date();
defaultStartDate.setHours(0, 0, 0, 0);

export const detailsStepDefaultState: DetailsStepState = {
  activeDaysOfTheWeek: allDaysInactive,
  availableLocIds: [],
  availableOnline: true,
  customerTypeIds: [],
  discountGroupIds: [],
  endDate: null,
  endTime: '',
  firstTimeCustomerOption: DiscountFirstTimeCustomer.NoFirstTimeDiscount,
  limitPerCustomer: false,
  limitPerCustomerQuantity: '',
  paymentRestrictions: {
    PayByBankSignupIncentive: false,
  },
  platformTypeRestrictions: [],
  redemptionLimit: '',
  requireManagerApproval: false,
  restrictByDayOfWeek: false,
  segmentIds: [],
  specifyEndDate: false,
  startDate: defaultStartDate,
  startTime: '',
  visited: false,
};

export function detailsStepReducer(state: DetailsStepState, action: DetailsStepAction) {
  switch (action.type) {
    case DetailsStepActions.INITIALIZE:
      return {
        ...detailsStepDefaultState,
        ...action.payload,
      };
    case DetailsStepActions.RESET:
      return {
        ...detailsStepDefaultState,
      };
    case DetailsStepActions.SET_ACTIVE_DAYS_OF_THE_WEEK:
      return {
        ...state,
        activeDaysOfTheWeek: action.payload,
      };
    case DetailsStepActions.SET_AVAILABLE_LOC_IDS:
      return {
        ...state,
        availableLocIds: action.payload,
      };
    case DetailsStepActions.SET_AVAILABLE_ONLINE:
      return {
        ...state,
        availableOnline: action.payload,
      };
    case DetailsStepActions.SET_ORDER_SOURCE_IDS:
      return {
        ...state,
        platformTypeRestrictions: action.payload.map((restriction) => ({
          PlatformTypeId: restriction.PlatformTypeId,
          IsExclusion: false,
        })),
      };
    case DetailsStepActions.SET_CUSTOMER_TYPES:
      return {
        ...state,
        customerTypeIds: action.payload,
      };
    case DetailsStepActions.SET_DISCOUNT_GROUPS:
      return {
        ...state,
        discountGroupIds: action.payload,
      };
    case DetailsStepActions.SET_END_DATE:
      return {
        ...state,
        endDate: action.payload,
      };
    case DetailsStepActions.SET_END_TIME:
      return {
        ...state,
        endTime: action.payload,
      };
    case DetailsStepActions.SET_FIRST_TIME_CUSTOMER_OPTION:
      return {
        ...state,
        firstTimeCustomerOption: action.payload,
      };
    case DetailsStepActions.SET_LIMIT_PER_CUSTOMER:
      return {
        ...state,
        limitPerCustomer: action.payload,
      };
    case DetailsStepActions.SET_LIMIT_PER_CUSTOMER_QUANTITY:
      return {
        ...state,
        limitPerCustomerQuantity: action.payload,
      };
    case DetailsStepActions.SET_PAYMENT_RESTRICTION:
      return {
        ...state,
        paymentRestrictions: {
          ...state.paymentRestrictions,
          [action.payload.restriction]: action.payload.enabled,
        },
      };
    case DetailsStepActions.SET_REDEMPTION_LIMIT:
      return {
        ...state,
        redemptionLimit: action.payload,
      };
    case DetailsStepActions.SET_REQUIRE_MANAGER_APPROVAL:
      return {
        ...state,
        requireManagerApproval: action.payload,
      };
    case DetailsStepActions.SET_RESTRICT_BY_DAY_OF_WEEK:
      return {
        ...state,
        restrictByDayOfWeek: action.payload,
      };
    case DetailsStepActions.SET_SEGMENTS:
      return {
        ...state,
        segmentIds: action.payload,
      };
    case DetailsStepActions.SET_SPECIFY_END_DATE:
      return {
        ...state,
        specifyEndDate: action.payload,
      };
    case DetailsStepActions.SET_START_DATE:
      return {
        ...state,
        startDate: action.payload,
      };
    case DetailsStepActions.SET_START_TIME:
      return {
        ...state,
        startTime: action.payload,
      };
    case DetailsStepActions.VISITED:
      return {
        ...state,
        visited: true,
      };
    default:
      return state;
  }
}
